import { Snackbar } from 'components/Snackbar';
import { useLoader } from 'hooks/utilHooks';
import { createContext, ReactNode, useState } from 'react';
import { AdminReportService } from 'services';
import { IPaginatedRequest } from 'types/Util';

import { AlertColor } from '@mui/material';
import { FormProvider, useForm } from 'react-hook-form';
import { AdminReportDefaultValues, IAdminReport, IAdminReportResult } from 'types/AdminReport';

export interface IAdminReportContext extends IAdminReportResult {
  loading: boolean;
  loadingDelete: boolean;
  loadingView: boolean;
  handlePreviousPage: () => void;
  handleNextPage: () => void;
  handleRefresh: (startDate: string | null, endDate: string | null, planId: string | null) => void;
  setSnackbar: Function;
  adminReport: IAdminReport | null;
}

interface ISnackbar {
  message: string | null;
  variant?: AlertColor | undefined;
}

export const AdminReportContext = createContext<IAdminReportContext>(
  {} as IAdminReportContext
);

type Props = {
  children: ReactNode;
};

export const AdminReportProvider = ({ children }: Props) => {
  const [adminReports, setAdminReports] = useState<IAdminReportResult>({
    docs: [],
  } as unknown as IAdminReportResult);
  
  const [adminReport, setAdminReport] = useState<IAdminReport | null>(
    null
  );
  const [startdate, setstartdate] = useState<any>();
  const [enddate, setenddate] = useState<any>();
  const [planid, setplanid] = useState<any>();

  const { loading, showLoader, hideLoader } = useLoader(false);
  const loadingDelete = useLoader(false);
  const loadingView = useLoader(false);

  const [snackbar, setSnackbar] = useState<ISnackbar>({
    message: '',
    variant: 'info',
  });

  const form = useForm<IAdminReport>({
    defaultValues: AdminReportDefaultValues,

    mode: 'onBlur',
  });


  const getAdminReports = async ({ page, limit, startdate, enddate, planid }: IPaginatedRequest) => {
    setAdminReport(null);

    try {
      showLoader();

      const reports = await AdminReportService.getReport({ page, limit, startdate, enddate, planid })
      setAdminReports(reports)
    } catch (error) {
      const { message } = error as Error;

      setSnackbar({
        message,
        variant: 'error',
      });
    } finally {
      hideLoader();
    }
  };
  
  const handlePreviousPage = () => {
    getAdminReports({
      page: adminReports?.prevPage,
      startdate,
      enddate,
      planid
    });
  };

  const handleNextPage = () => {
    getAdminReports({
      page: adminReports?.nextPage,
      startdate,
      enddate,
      planid
    });
  };

  const handleRefresh = (startDate: string | null, endDate: string | null, planId: string | null) => {
    getAdminReports({startdate: startDate, enddate: endDate, planid: planId});
    setstartdate(startDate);
    setenddate(endDate);
    setplanid(planId);
  };

  return (
    <AdminReportContext.Provider
      value={{
        loading,
        handlePreviousPage,
        handleNextPage,
        handleRefresh,
        setSnackbar,
        loadingDelete: loadingDelete.loading,
        loadingView: loadingView.loading,
        adminReport,
        ...adminReports,
      }}
    >

      <FormProvider {...form}>{children}</FormProvider>
      <Snackbar
        text={snackbar.message}
        snackbarOpen={Boolean(snackbar.message)}
        setSnackbarOpen={() => {
          setSnackbar({
            message: '',
          });
        }}
        severityType={snackbar.variant}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      />
    </AdminReportContext.Provider>
  );
};
